import {useContext, useEffect, useRef} from 'react'
import {format, parseISO} from 'date-fns'
import LoadingStateContext from '../../context/LoadingContext'
import {useToast} from '../../../../../_fds/layout/components/toast-messages'
import {useStore} from '../../store'
import {LaborProposalApplyMode, LaborScheduleActualModel} from '../../core/_models'
import {
  applyProposal,
  dismissProposal,
  getLaborScheduleActual,
  getPendingProposal,
  getWeeklyShifts,
  markProposalSeen,
} from '../../core/_requests'
import {convertLaborShifts} from '../../helpers/generals'

/**
 * The recommended-schedule prompt: ask the backend once per store whether a
 * machine-generated schedule is worth showing, then open it exactly once.
 *
 * All the "should this be shown?" rules live server-side (past weeks, proposals
 * that don't beat the real schedule, location-weeks already seen/dismissed —
 * and the materially-better exception that lets a genuinely improved run ask
 * again). The client's only job is to fetch on store change, auto-open the
 * first time a given proposal appears, and record what the manager decided.
 */
export const useRecommendedSchedule = () => {
  const {
    pendingProposal,
    proposalModalOpen,
    selectedLocationId,
    handleState,
    handleMultipleStates,
  } = useStore()

  const openModal = () => handleState(true, 'proposalModalOpen')
  const closeModal = () => handleState(false, 'proposalModalOpen')

  /**
   * Record that the recommendation reached the screen. Called when the modal
   * opens rather than from a button, so closing it any way at all — Later, the
   * X, the backdrop — counts as shown and stops it re-opening. The
   * recommendation itself stays available from the Options menu.
   *
   * Skipped once anything is on record against THIS proposal, not just a prior
   * 'seen': a rejected recommendation is still reopenable from that menu, and
   * writing a read receipt over the rejection would undo it for the whole
   * store. A decision about an OLDER run still gets a receipt — that is a
   * materially better proposal being surfaced, and its receipt is what stops it
   * re-opening on every load. (The backend refuses the same downgrade — this
   * just saves the round trip.)
   */
  const markSeen = async () => {
    if (!pendingProposal || pendingProposal.decision?.proposal_uuid === pendingProposal.uuid) {
      return
    }
    await markProposalSeen(pendingProposal.uuid)
    handleState(
      {
        ...pendingProposal,
        decision: {
          decision: 'seen',
          decided_at: null,
          decided_by_id: null,
          reason: null,
          proposal_uuid: pendingProposal.uuid,
        },
      },
      'pendingProposal'
    )
  }

  /**
   * "Dismiss": rejected for this week — survives the nightly regeneration, and
   * applies to the whole store rather than the person who clicked it.
   *
   * The proposal stays in the store rather than being cleared: rejecting says
   * "don't apply this", not "hide it". It keeps its place in the Options menu
   * (the backend keeps serving it from /pending with auto_open false) so a
   * manager who changes their mind still has a way back to it — the decision
   * recorded here is what makes the modal and the menu label it as rejected.
   */
  const rejectProposal = async (reason?: string) => {
    if (!pendingProposal) return
    await dismissProposal(pendingProposal.uuid, reason)
    handleMultipleStates(
      new Map<string, any>([
        ['proposalModalOpen', false],
        [
          'pendingProposal',
          {
            ...pendingProposal,
            decision: {
              decision: 'dismissed',
              decided_at: null,
              decided_by_id: null,
              reason: reason ?? null,
              proposal_uuid: pendingProposal.uuid,
            },
          },
        ],
      ])
    )
  }

  /**
   * "Apply": copy the recommendation into the real schedule, then move the
   * board onto that week and reload it. The reload is explicit rather than
   * left to the week-change effect — applying to the week already on screen
   * changes no dependency, so nothing would refetch.
   */
  const acceptProposal = async (
    mode: LaborProposalApplyMode
  ): Promise<LaborScheduleActualModel | undefined> => {
    if (!pendingProposal || !selectedLocationId) return undefined
    const weekDate = parseISO(pendingProposal.week_start)
    await applyProposal(pendingProposal.uuid, mode)

    const [shifts, schedule] = await Promise.all([
      getWeeklyShifts(weekDate, selectedLocationId),
      getLaborScheduleActual(selectedLocationId, weekDate),
    ])
    handleMultipleStates(
      new Map<string, any>([
        ['selectedDate', weekDate],
        ['events', convertLaborShifts(shifts || [])],
        ['selectedLaborScheduleId', schedule?.id],
        ['proposalModalOpen', false],
        ['pendingProposal', null],
      ])
    )
    return schedule
  }

  return {
    pendingProposal,
    proposalModalOpen,
    openModal,
    closeModal,
    markSeen,
    rejectProposal,
    acceptProposal,
  }
}

/**
 * Fetch-and-auto-open side of the prompt — mount once, next to the board.
 */
export const useRecommendedScheduleEffects = () => {
  const {selectedLocationId, selectedDate, templateMode, handleMultipleStates} = useStore()
  // The week on screen decides which recommendation is carried: the board opens
  // on next week and gets next week's; stepping to another week gets that
  // week's own, or none.
  const dayKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null
  // Auto-open is per proposal, not per mount: switching stores back and forth
  // must not re-pop a recommendation the manager already has on screen once.
  const autoOpenedRef = useRef<string | null>(null)

  useEffect(() => {
    // Template mode swaps the board for a client-side draft — there is no real
    // schedule on screen to recommend against, and the modal would fight the
    // draft's leave-guard.
    if (!selectedLocationId || templateMode) return

    let cancelled = false
    const fetchPending = async () => {
      const pending = await getPendingProposal(selectedLocationId, selectedDate)
      if (cancelled) return
      const proposal = pending?.proposal ?? null
      const states = new Map<string, any>([['pendingProposal', proposal]])
      if (proposal && pending?.auto_open && autoOpenedRef.current !== proposal.uuid) {
        autoOpenedRef.current = proposal.uuid
        // Only ever auto-OPEN: a modal the user already has open must not be
        // closed out from under them by a background refetch.
        states.set('proposalModalOpen', true)
      }
      handleMultipleStates(states)
    }
    fetchPending()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, dayKey, templateMode])
}

/** Loading-state + toast wrapper shared by the modal's actions. */
export const useProposalActionRunner = () => {
  const {setLoadingState} = useContext(LoadingStateContext)
  const {showToast} = useToast()

  return async (
    action: () => Promise<any>,
    success: {title: string; message: string},
    failure: string
  ) => {
    setLoadingState(true)
    try {
      await action()
      showToast(success.title, success.message, 'success')
    } catch (error) {
      showToast('Error', failure, 'danger')
    }
    setTimeout(() => setLoadingState(false), 10)
  }
}
